<script lang="ts">
  /**
   * The five-column table: Document (name, path, provider logo) · Exposure · Categories ·
   * Findings (spec 6.2) · Status (spec 9.6, #2251).
   *
   * It renders one already-trimmed, already-sorted, already-paginated page. Trimming to the
   * active info-type list, sorting and paging are all decided by the container over the fully
   * loaded set, so this component owns nothing but layout: it reports clicks and draws what it
   * is given. In particular the Findings number is the trimmed sum the container handed over,
   * never `row.sdpTotalFindings` (spec 6.2, "Findings column").
   *
   * A row is clickable (and Enter/Space-activatable): it reports the underlying document through
   * `onSelect`, and draws the row named by `selectedRowId` as pressed. Which row is selected, and
   * when that selection ends, is the container's decision.
   *
   * Rows whose provider the last run skipped are dimmed. They are never dropped - the reviewer
   * needs to see them, just not to mistake them for current - and the warning above the table
   * says why.
   *
   * The Status cell is plain capitalised text from `displayedStatusOf`; only Reopened is
   * emphasised (amber). Until the status read lands every row shows Open, and a failed read
   * shows "—" with the tooltip "Status unavailable" rather than a status the reviewer might act
   * on (spec 9.6, I14).
   *
   * The two page arrows are local rather than `$ui/Pagination/PaginationControls.svelte` on
   * purpose: that control always renders a rows-per-page select and a "Page N" indicator, and
   * the spec fixes the page size at 25 and asks for one summary, "1-25 of N items", top right of
   * the table header (6.2). Adopting it would put back the second page indicator this build was
   * asked to remove and offer a page size the page does not support.
   */
  import { formatNumberWithCommas } from "$lib/utils/number";

  import { Info } from "lucide-svelte";

  import { Tooltip, TooltipContent, TooltipTrigger } from "$lib/ui/tooltip";

  import SortableColumnHeader from "../SortableColumnHeader.svelte";
  import SystemLogo from "../SystemLogo.svelte";
  import type { NamedCustomInfoType } from "../categoryLabels";
  import {
    pageSummary,
    providerLabel,
    provisionalSuffix,
    type ExposedSensitiveDocument,
    type PageOfRows,
    type Provider,
    type RowSort,
    type SortColumn,
    type SortDirection,
  } from "../exposedSensitiveDocuments";
  import { type TrimmedExposureRow } from "./activeInfoTypes";
  import ExposureBadge from "./ExposureBadge.svelte";
  import CategoryCountChips from "./CategoryCountChips.svelte";
  import {
    displayedStatusOf,
    STATUS_LABELS,
    STATUS_UNAVAILABLE,
    STATUS_UNAVAILABLE_GLYPH,
    STATUS_UNAVAILABLE_LABEL,
    type StatusLoadState,
  } from "./status";

  let {
    page,
    provisional = false,
    sort,
    onSort,
    onPageChange,
    skippedProviders = [],
    selectedRowId = null,
    onSelect,
    customInfoTypes = [],
    statusState = "ready",
  }: {
    /**
     * A page of rows already trimmed to the active info-type list (#2206): the Categories and
     * Findings columns render the trimmed map and its sum, which equal the row's own values
     * under show-all.
     */
    page: PageOfRows<TrimmedExposureRow>;
    /** The rows are still loading, so the summary's total is only the rows so far (#2265). */
    provisional?: boolean;
    sort: RowSort;
    onSort: (column: SortColumn, direction: SortDirection) => void;
    onPageChange: (direction: "next" | "prev") => void;
    /** Providers the last run skipped; their rows render dimmed. */
    skippedProviders?: Provider[];
    /** The row whose detail panel is open, if any; it renders highlighted and pressed. */
    selectedRowId?: string | null;
    /** Called with the whole row when the reviewer opens it. Omit to leave the rows inert. */
    onSelect?: (row: ExposedSensitiveDocument) => void;
    /** The org's enabled custom info types, so a row chips them as their own category. */
    customInfoTypes?: NamedCustomInfoType[];
    /** Where the org's status read is; rows carry their joined record on `row.status`. */
    statusState?: StatusLoadState;
  } = $props();

  // The header buttons report their column as a string; every one of them is a SortColumn.
  const sortBy = (column: string, direction: SortDirection) => onSort(column as SortColumn, direction);
  const isSkipped = (provider: Provider | undefined) => provider !== undefined && skippedProviders.includes(provider);
  const INSTANCES_HINT =
    "Instances of sensitive data found in this document, counting only the categories selected above.";
  const gridCols = "grid-cols-[minmax(0,5fr)_9rem_minmax(0,4fr)_7rem_7rem]";

  // A row is a button, not a link: it opens a panel beside the table rather than navigating, and
  // there is no URL for a selected row (spec 6.4, and deep-linking is explicitly not required).
  // The pressed state is `aria-pressed`, matching `ExposureCounterCard`; `aria-selected` is not a
  // supported attribute of `role="button"` and would only add an a11y warning.
  const onRowKeydown = (event: KeyboardEvent, row: ExposedSensitiveDocument) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault(); // Space would otherwise scroll the page.
    onSelect?.(row);
  };
</script>

<div class="rounded-lg bg-white shadow overflow-hidden" data-testid="exposed-documents-table">
  <div class="flex items-center gap-3 px-3 py-2 border-b border-gray-200 justify-end">
    <span class="inline-flex items-center gap-1">
      <button
        type="button"
        class="px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-40"
        disabled={page.start <= 1}
        onclick={() => onPageChange("prev")}
        aria-label="Previous page"
      >
        &lsaquo;
      </button>
      <button
        type="button"
        class="px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-40"
        disabled={page.end >= page.total}
        onclick={() => onPageChange("next")}
        aria-label="Next page"
      >
        &rsaquo;
      </button>
    </span>
    <span class="text-xs text-gray-600 tabular-nums whitespace-nowrap" data-testid="page-summary">
      {pageSummary(page)}{provisionalSuffix(provisional)}
    </span>
  </div>

  <div class="grid {gridCols} gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-300 items-center">
    <SortableColumnHeader
      column="document"
      label="Document"
      sortField={sort.column}
      sortDirection={sort.direction}
      onSort={sortBy}
    />
    <span class="text-xs font-medium text-gray-500 tracking-wider">Exposure</span>
    <span class="text-xs font-medium text-gray-500 tracking-wider">Categories</span>
    <span class="flex items-center gap-1">
      <span class="flex-1 min-w-0">
        <SortableColumnHeader
          column="findings"
          label="Instances"
          sortField={sort.column}
          sortDirection={sort.direction}
          onSort={sortBy}
        />
      </span>
      <Tooltip>
        <TooltipTrigger aria-label="What instances means" data-testid="instances-info">
          <Info class="h-3.5 w-3.5 text-gray-400" />
        </TooltipTrigger>
        <TooltipContent>
          <span class="text-xs">{INSTANCES_HINT}</span>
        </TooltipContent>
      </Tooltip>
    </span>
    {#if statusState === "failed"}
      <!-- No rank to sort on when the status read failed (spec 9.3), so the header is plain text. -->
      <span data-testid="status-header-unsortable" class="text-xs font-medium text-gray-500 tracking-wider">
        Status
      </span>
    {:else}
      <SortableColumnHeader
        column="status"
        label="Status"
        sortField={sort.column}
        sortDirection={sort.direction}
        onSort={sortBy}
      />
    {/if}
  </div>

  <div class="divide-y divide-gray-100">
    {#each page.items as { row, infoTypeCounts, findings } (row.id)}
      {@const status = displayedStatusOf(row, statusState)}
      <div
        class="grid {gridCols} gap-3 px-3 py-1.5 text-xs items-center cursor-pointer hover:bg-zinc-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-gray-400 {selectedRowId ===
        row.id
          ? 'bg-gray-100'
          : ''} {isSkipped(row.provider) ? 'opacity-60' : ''}"
        data-testid="exposed-document-row"
        data-resource-id={row.resourceId}
        data-row-id={row.id}
        data-dimmed={isSkipped(row.provider) ? "true" : "false"}
        data-selected={selectedRowId === row.id ? "true" : "false"}
        role="button"
        tabindex="0"
        aria-pressed={selectedRowId === row.id}
        aria-label="Open the detail panel for {row.name}"
        onclick={() => onSelect?.(row)}
        onkeydown={(event) => onRowKeydown(event, row)}
      >
        <span class="flex items-center gap-2 min-w-0">
          {#if row.provider}
            <span
              class="shrink-0 [&>div]:w-6 [&>div]:h-6 [&>div]:border-0 [&>div>span]:w-3.5 [&>div>span]:h-3.5"
              title={providerLabel(row.provider)}
            >
              <SystemLogo system={row.provider} />
            </span>
          {/if}
          <span class="min-w-0">
            <span class="block truncate font-semibold">{row.name}</span>
            <span class="block truncate font-mono text-[10px] text-gray-500">{row.path}</span>
          </span>
        </span>
        <span class="min-w-0 overflow-hidden" data-testid="exposure-cell"><ExposureBadge {row} /></span>
        <CategoryCountChips counts={infoTypeCounts} {customInfoTypes} />
        <span class="tabular-nums font-bold" data-testid="instances-cell">{formatNumberWithCommas(findings)}</span>
        <span
          class={status === STATUS_UNAVAILABLE
            ? "text-gray-500"
            : status === "reopened"
              ? "font-medium text-amber-700"
              : "text-offblack"}
          title={status === STATUS_UNAVAILABLE ? STATUS_UNAVAILABLE_LABEL : undefined}
          data-testid="status-cell"
          data-status={status}>{status === STATUS_UNAVAILABLE ? STATUS_UNAVAILABLE_GLYPH : STATUS_LABELS[status]}</span
        >
      </div>
    {/each}
  </div>
</div>
